/* ============================================================
   Happy Monkfruit — homepage interactivity
   Replaces the Claude Design {{ bindings }} with real vanilla JS.
   - scroll reveal (IntersectionObserver)
   - promo strip rotator
   - press-quote rotator
   - mobile menu toggle
   - sticky nav shadow on scroll -> now lives in sections/hmf-header.liquid
   - AJAX add-to-cart -> Shopify Cart API (live badge + toast)
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function path(p) { return (ROOT_URL.replace(/\/$/, '')) + p; }

  /* ---- Scroll reveal ---- */
  function initReveal() {
    var els = document.querySelectorAll('.hmf-reveal');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('hmf-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('hmf-in'); io.unobserve(en.target); }
      });
    }, { threshold: 0, rootMargin: '300px 0px 300px 0px' });
    els.forEach(function (e) { io.observe(e); });
  }

  /* ---- Generic rotator (promo strip + press quotes) ---- */
  function initRotator(root) {
    var items = root.querySelectorAll('[data-rot-item]');
    if (!items.length) return;
    var i = 0;
    var interval = parseInt(root.getAttribute('data-rot-interval') || '4200', 10);
    var prev = root.querySelector('[data-rot-prev]');
    var next = root.querySelector('[data-rot-next]');
    var logos = root.querySelectorAll('[data-rot-logo]');
    var timer = null;

    function show(n) {
      i = (n + items.length) % items.length;
      items.forEach(function (el, idx) {
        el.style.display = idx === i ? '' : 'none';
        // Replay the fade-in by toggling a class. Do NOT touch el.style.animation:
        // setting it to '' deletes the inline declaration outright, so the very
        // first swap would kill the animation for good.
        if (idx === i) { el.classList.remove('hmf-rot-in'); void el.offsetWidth; el.classList.add('hmf-rot-in'); }
      });
      if (logos.length) {
        logos.forEach(function (logo) {
          var idx = parseInt(logo.getAttribute('data-rot-logo'), 10);
          var on = idx === i;
          logo.style.opacity = on ? '1' : '0.45';
          logo.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }
    function start() {
      if (reduceMotion) return;
      stop();
      timer = setInterval(function () { show(i + 1); }, interval);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    if (prev) prev.addEventListener('click', function () { show(i - 1); start(); });
    if (next) next.addEventListener('click', function () { show(i + 1); start(); });

    // Each logo selects its own quote. Without this the logos were purely
    // decorative — they lit up on the timer but were not clickable at all.
    logos.forEach(function (logo) {
      logo.addEventListener('click', function () {
        var idx = parseInt(logo.getAttribute('data-rot-logo'), 10);
        if (isNaN(idx)) return;
        show(idx);
        start();
      });
    });

    // Pause the carousel while the visitor is reading or hovering a logo.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    show(0);
    start();
  }

  /* ---- Mobile menu ---- */
  function initMenu() {
    var btn = document.querySelector('[data-hmf-menu-btn]');
    var menu = document.querySelector('[data-hmf-mobile-menu]');
    if (!btn || !menu) return;
    btn.addEventListener('click', function () {
      var open = menu.getAttribute('data-open') === 'true';
      menu.setAttribute('data-open', open ? 'false' : 'true');
      menu.style.display = open ? 'none' : 'block';
    });
    menu.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', function () {
        menu.setAttribute('data-open', 'false');
        menu.style.display = 'none';
      });
    });
  }

  /* ---- Sticky nav shadow ----
     Removed. sections/hmf-header.liquid owns this behaviour: it holds the
     shadow state in a `shadowOn` boolean and only touches nav.style.boxShadow
     inside a requestAnimationFrame when that boolean actually flips.
     The handler that used to live here was a second, unthrottled writer on
     the same sticky element: it re-assigned an inline box-shadow on EVERY
     scroll event, with no rAF and no state guard, forcing a style recalc and
     a repaint of a position:sticky element per event — and it wrote a
     different shadow value than the header does, so the two fought each
     other. Do not reintroduce it here. ---- */

  /* ---- Cart badge ---- */
  function paintCount(n) {
    document.querySelectorAll('[data-hmf-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.style.display = n > 0 ? 'flex' : 'none';
    });
  }
  function refreshCartCount() {
    return fetch(path('/cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { paintCount(c.item_count); return c; })
      .catch(function () { return null; });
  }

  /* ---- Confirmation toast ---- */
  var toastEl = null, toastTimer = null;
  function toast(message, withCartLink) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%) translateY(14px)',
        'z-index:9999', 'display:flex', 'align-items:center', 'gap:14px',
        'padding:13px 20px', 'border-radius:999px', 'background:#115e46', 'color:#fffbf1',
        'font-family:"Nunito Sans",system-ui,sans-serif', 'font-size:15px', 'font-weight:600',
        'box-shadow:0 18px 40px -12px rgba(19,53,42,.55)', 'opacity:0',
        'transition:opacity .22s ease,transform .22s ease', 'pointer-events:auto',
        'max-width:calc(100vw - 32px)'
      ].join(';');
      document.body.appendChild(toastEl);
    }
    var link = withCartLink
      ? '<a href="' + path('/cart') + '" style="color:#ffd9a0;text-decoration:underline;font-weight:700;white-space:nowrap">View cart</a>'
      : '';
    toastEl.innerHTML =
      '<span style="display:inline-flex;align-items:center;gap:9px">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 12 10 18 20 6"></polyline></svg>' +
        '<span>' + message + '</span>' +
      '</span>' + link;
    requestAnimationFrame(function () {
      toastEl.style.opacity = '1';
      toastEl.style.transform = 'translateX(-50%) translateY(0)';
    });
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.style.opacity = '0';
      toastEl.style.transform = 'translateX(-50%) translateY(14px)';
    }, 4200);
  }

  /* ---- AJAX add to cart ---- */
  function initAddToCart() {
    document.querySelectorAll('[data-hmf-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-hmf-add');
        if (!id) return;
        var original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        fetch(path('/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
        })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error((res.j && res.j.description) || 'add failed');
            btn.textContent = 'Added ✓';
            refreshCartCount();
            toast('Added to cart', true);
            document.dispatchEvent(new CustomEvent('hmf:cart:added', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            setTimeout(function () {
              btn.textContent = original;
              btn.disabled = false;
            }, 1600);
          })
          .catch(function () {
            // Fallback: real form POST so the customer still reaches the cart.
            var f = document.createElement('form');
            f.method = 'post';
            f.action = path('/cart/add');
            f.innerHTML = '<input type="hidden" name="id" value="' + parseInt(id, 10) + '"><input type="hidden" name="quantity" value="1">';
            document.body.appendChild(f);
            f.submit();
          });
      });
    });
  }

  function init() {
    initReveal();
    document.querySelectorAll('[data-rot]').forEach(initRotator);
    initMenu();
    initAddToCart();
    // Badge may be stale if the page was served from bfcache or a full-page cache.
    if (document.querySelector('[data-hmf-cart-count]')) refreshCartCount();
  }

  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
